import React, { useEffect, useState } from 'react';
import { IconInitiate } from '../../../../../../../assets/icons/icon-initiate';
import { ControlWrapper, InitiateButton, SubmitButton } from '../../../../../../../components';
import { defaultFavouriteSetOnTimeList } from '../../../../../../../const';
import {
  ChannelActionParameterInput,
  ChannelTypeInternal,
  TriggerActionRequestInput,
  TriggerActionType,
} from '../../../../../../../data-access/gql-types/graphql';
import { SwitchDetailsTimeActivations } from '../../../../../components/channel-details/switch/time-activatons';
import { ChannelType, ChannelLightType, FavouritePositions } from '../../../../../types';
import { parseFavouritePositions } from '../../../../../utils/channels/helpers';
import { useTriggerFormContext } from '../../../context';
import { addAndReplaceInFavouritePositions } from '../../../utils';
import { useOnOffControl } from '../helpers/use-control';

type LightSetupProps = {
  setAction: (value: TriggerActionRequestInput) => void;
  channel: ChannelType;
  defaultParameters: TriggerActionRequestInput | undefined;
};

const LightSetup: React.FC<LightSetupProps> = ({ setAction, channel, defaultParameters }) => {
  const [ChannelParameters, setChannelParameters] = useState<{ [key: string]: ChannelActionParameterInput }>({});
  const [statusOn, setStatusOn] = useState<boolean>(false);
  const [activeOnTime, setActiveOnTime] = useState<number | null>(null);
  const [favoritesList, setFavoritesList] = useState<FavouritePositions>(defaultFavouriteSetOnTimeList);
  const { setOff, setOn } = useOnOffControl(ChannelParameters);
  const { updateAction } = useTriggerFormContext();
  const channelData = channel.data as ChannelLightType;

  useEffect(() => {
    if (defaultParameters) {
      switch (defaultParameters.actionType) {
        case TriggerActionType.SetLightState: {
          setStatusOn(defaultParameters?.booleanValue ?? false);
          setChannelParameters(defaultParameters?.booleanValue ? setOn() : setOff());
          break;
        }
        case TriggerActionType.SetLightSetOnTime: {
          setStatusOn(true);
          setChannelParameters(setOn());
          setActiveOnTime(defaultParameters.integerValue || 0);
          break;
        }
      }
    }
  }, [setStatusOn, defaultParameters]);

  useEffect(() => {
    if (channelData.type === ChannelTypeInternal.Light) {
      const list = parseFavouritePositions(channelData.favouritePositionList, defaultFavouriteSetOnTimeList, false);
      setFavoritesList(addAndReplaceInFavouritePositions(list, defaultParameters?.integerValue));
    } else {
      setFavoritesList(
        addAndReplaceInFavouritePositions(defaultFavouriteSetOnTimeList, defaultParameters?.integerValue),
      );
    }
  }, [channelData.favouritePositionList, defaultParameters?.integerValue]);

  const handleControlOn = (value: boolean) => {
    setChannelParameters(value ? setOn() : setOff());
    setStatusOn(value);
  };

  const onSubmit = () => {
    const data = {
      id: channel.id,
      actionType: activeOnTime ? TriggerActionType.SetLightSetOnTime : TriggerActionType.SetLightState,
      ...(activeOnTime ? { integerValue: activeOnTime } : { booleanValue: statusOn }),
    };

    updateAction(channel.id, data);
    setAction(data);
  };

  const onFavouriteClick = (value) => {
    if (activeOnTime === value) {
      setActiveOnTime(null);
    } else {
      setActiveOnTime(value);
      setChannelParameters(setOn());
      setStatusOn(true);
    }
  };

  const onTimeChange = (value: number, index: number) => {
    setActiveOnTime(value);

    setFavoritesList((prev) => {
      const updated: FavouritePositions = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  return (
    <>
      <ControlWrapper>
        <InitiateButton isOn={statusOn} onClickControl={handleControlOn}>
          <IconInitiate />
        </InitiateButton>
      </ControlWrapper>
      <SwitchDetailsTimeActivations
        setOnTimeList={favoritesList}
        handleSetOnTime={onFavouriteClick}
        active={activeOnTime}
        onTimeChange={onTimeChange}
      />
      <SubmitButton onClick={onSubmit} />
    </>
  );
};

export default LightSetup;
